import Component from '@glimmer/component';
import { action, get } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import GoalValidations from 'scaleflow/validators/goal';
import goalValidations from 'scaleflow/validators/goal';
import targetValidations from 'scaleflow/validators/target';
import { Changeset } from 'ember-changeset';
import { assert } from '@ember/debug';

export default class GoalsWizardCreateComponent extends Component {
  @service router;
  @service store;
  @service betaToggle;
  goalValidations = GoalValidations;

  @tracked activeStep = 0;
  @tracked goalChangeset = {};
  @tracked targetChangesets = [];
  targetChangesetFactory;
  @tracked selectedGoals = [];
  @tracked selectedMetrics = [];

  @tracked goalSearch = '';
  @tracked metricSearch = '';
  @tracked goal = null;

  @action
  setActiveStep(index) {
    this.activeStep = index;
  }

  get activePeopleInOrganisation() {
    return this.store.peekAll('person').filter((p) => p.active);
  }

  get activeGoalsInOrganisation() {
    return this.store.peekAll('goal').filter((g) => !g.isNew);
  }

  get goals_without_parents() {
    return this.activeGoalsInOrganisation.filter(
      (goal) => !goal?.parent?.content,
    );
  }

  get metrics() {
    return this.store.peekAll('metric');
  }

  get personsList() {
    return this.store.peekAll('person').filter((p) => p.active);
  }

  @action
  handleOwnerSelect(changeset, item) {
    changeset.set('owner', item);
  }

  @action
  clearOwner(changeset) {
    changeset.set('owner', null);
  }

  @action
  handleStatusSelect(changeset, val) {
    changeset.set('active', val);
  }

  @action
  onDescriptionChange(changeset, e) {
    changeset.set('description', e);
  }

  @action
  closeGoalWizard() {
    if (this.args.closeWizard && typeof this.args.closeWizard === 'function') {
      this.args?.closeWizard?.();
    } else {
      assert(
        "Please pass a prop named 'closeWizard' with a callback function to the Goals Wizard component",
      );
    }
  }

  @action
  cancelForm() {
    this.goalChangeset.rollback();
    this.goal.deleteRecord();
    this.targetChangesets.forEach((target) => {
      target.rollback();
      target.data.deleteRecord();
    });
    this.closeGoalWizard();
  }

  @action
  rollbackChanges(changeset) {
    changeset.rollback();
  }

  @action
  handleGoalsSelect(goal, e) {
    const checked = e.target.checked;
    if (checked === undefined) return;
    if (checked) this.selectedGoals = [goal];
    else this.selectedGoals = [];
  }

  @action
  unlinkParentGoal() {
    this.selectedGoals = [];
    this.goalChangeset.set('parent', null);
  }

  @action
  handleMetricSelect(metric, e) {
    const checked = e.target.checked;
    if (checked === undefined) return;
    const metrics = this.selectedMetrics;
    const targetChangesets = this.targetChangesets;
    if (checked) {
      metrics.push(metric);
      targetChangesets.push(this.targetChangesetFactory());
    } else {
      const index = metrics.findIndex((_metric) => _metric.id === metric.id);
      if (index < 0) return;
      else {
        metrics.splice(index, 1);
        targetChangesets.splice(index, 1);
      }
    }
    this.selectedMetrics = metrics;
    this.targetChangesets = targetChangesets;
  }

  @action
  async saveGoal() {
    const goal = this.goal;
    goal.mapChangesetToAttributes(this.goalChangeset.changes);
    if (this.selectedGoals.length) goal.set('parent', this.selectedGoals[0]);
    await goal.save();
    const promises = [];
    for (let i = 0; i < this.targetChangesets.length; i++) {
      const target = this.targetChangesets[i];
      target.set('goal', this.goal);
      target.set('metric', this.selectedMetrics[i]);
      promises.push(target.save());
    }
    await Promise.all(promises);
    this.closeGoalWizard();
  }

  goalValidator({ key, newValue, oldValue, changes, content }) {
    let validator = get(goalValidations, key);
    if (typeof validator === 'function') {
      return validator(newValue, oldValue, changes, content);
    }
  }

  targetValidator({ key, newValue, oldValue, changes, content }) {
    let validator = get(targetValidations, key);
    if (typeof validator === 'function') {
      return validator(newValue, oldValue, changes, content);
    }
  }

  resetForm() {
    this.activeStep = 0;
    this.targetChangesets = [];
    this.selectedGoals = [];
    this.selectedMetrics = [];
    this.goalSearch = '';
    this.metricSearch = '';
  }

  @action
  initializeForm() {
    this.goal = this.store.createRecord('goal');
    const goalChangeset = Changeset(
      this.goal.changeset_object,
      this.goalValidator,
    );
    const targetChangesetFactory = () => {
      const target = this.store.createRecord('target', {});
      return Changeset(target, this.targetValidator);
    };

    this.goalChangeset = goalChangeset;
    this.targetChangesetFactory = targetChangesetFactory;
    if (this.args.parentGoal) this.selectedGoals = [this.args.parentGoal];
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.resetForm();
  }
}
